import React, { useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import Lottie, { type LottieRefCurrentProps } from 'lottie-react';
import assistantAvatar from './assets/assistantAvatar.json';

type AvatarState = 'idle' | 'listening' | 'thinking' | 'responding';

type InteractiveLottieAvatarProps = {
  isOpen: boolean;
  state: AvatarState;
  onToggle: () => void;
};

const AvatarButton = styled.button<{ $state: AvatarState }>`
  position: fixed;
  right: max(16px, env(safe-area-inset-right, 0px));
  bottom: max(16px, env(safe-area-inset-bottom, 0px));
  z-index: 1150;
  width: 92px;
  height: 92px;
  border-radius: 20px;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 2px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: none;
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }

  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 2px;
  }

  @media (max-width: 600px) {
    right: max(12px, env(safe-area-inset-right, 0px));
    bottom: max(12px, env(safe-area-inset-bottom, 0px));
    width: 82px;
    height: 82px;
  }
`;

const AvatarInner = styled.div<{ $state: AvatarState }>`
  width: 100%;
  height: 100%;
  border-radius: 18px;
  overflow: visible;
  background: transparent;
  transform: ${({ $state }) => ($state === 'responding' ? 'scale(1.05)' : 'scale(1)')};
  transition: transform 0.2s ease;
`;

const stateLabel: Record<AvatarState, string> = {
  idle: 'idle',
  listening: 'listening',
  thinking: 'thinking',
  responding: 'responding',
};

const speedByState: Record<AvatarState, number> = {
  idle: 1,
  listening: 1.25,
  thinking: 1.8,
  responding: 1.35,
};

const InteractiveLottieAvatar: React.FC<InteractiveLottieAvatarProps> = ({ isOpen, state, onToggle }) => {
  const lottieRef = useRef<LottieRefCurrentProps>(null);
  const loopSegment = useMemo<[number, number]>(() => {
    const data = assistantAvatar as {
      op?: number;
      ip?: number;
      layers?: Array<{ op?: number; ip?: number }>;
    };
    const start = Number.isFinite(data.ip) ? Number(data.ip) : 0;
    const layerEnd = data.layers?.reduce((maxEnd, layer) => {
      const end = Number.isFinite(layer.op) ? Number(layer.op) : maxEnd;
      return Math.max(maxEnd, end);
    }, start);
    const jsonEnd = Number.isFinite(data.op) ? Number(data.op) : start + 1;
    const end = Math.max(start + 1, Math.min(jsonEnd, layerEnd ?? jsonEnd));
    return [start, end];
  }, []);

  useEffect(() => {
    lottieRef.current?.setSpeed(speedByState[state]);
  }, [state]);

  return (
    <AvatarButton
      type="button"
      onClick={onToggle}
      $state={state}
      aria-label={isOpen ? 'Close assistant chat' : 'Open assistant chat'}
      title={isOpen ? `Assistant (${stateLabel[state]}) - Close` : `Assistant (${stateLabel[state]}) - Open`}
    >
      <AvatarInner $state={state}>
        <Lottie
          lottieRef={lottieRef}
          animationData={assistantAvatar}
          loop
          autoplay
          initialSegment={loopSegment}
          style={{ width: '100%', height: '100%' }}
        />
      </AvatarInner>
    </AvatarButton>
  );
};

export default InteractiveLottieAvatar;
